/**
 * **O Diamante Serrilhado: a lâmina, desenhada a partir da arte da joia.**
 *
 * ## De onde a forma veio, e o que foi medido
 *
 * De `assets-source/jewels/jewel_saw.png`, olhada de perto. A esfera contém
 * **três serras** iguais, e cada uma é um **cata-vento de sete dentes** em torno
 * de um centro vazado em espiral. O que cada dente é, exatamente:
 *
 * - nasce largo junto ao cubo e afina até uma **ponta agudíssima** que se enrola
 *   para trás, como uma foice;
 * - a borda de ataque - a convexa - é lisa e é onde a luz corre;
 * - a borda de trás - a côncava - traz **uma farpa secundária** apontada de
 *   volta ao cubo. É essa farpa que se lê como "serrilhado" e não como "disco";
 * - o corpo é preto de obsidiana, com fios de luz quase brancos ao longo das
 *   arestas e um brilho frio nas cristas.
 *
 * Este módulo reconstrói essa silhueta como GEOMETRIA, e não copia um pixel da
 * joia: a arte da joia é da joia, e a `assets-source/` não pode ser cortada,
 * redimensionada nem re-codificada. O que atravessa é a forma.
 *
 * ## Quem manda desenhar, e quem nunca manda
 *
 * A autoridade. Este módulo recebe `{ x, y, step, radius }` por lâmina - onde
 * ela está, para que lado aponta e que tamanho tem - e mais nada. Não sabe o que
 * é um perfil, um socket, uma cópia, um nível ou uma joia, e não tem por onde
 * saber: não há campo onde isso caiba.
 *
 * E ele **não decide colisão nem dano**. O raio que recebe é o mesmo que a
 * simulação usou para decidir o contacto - `sawBladeRadius`, em `damage/saw.ts` -
 * portanto a lâmina que se vê é a lâmina que corta.
 */

/** Uma lâmina confirmada, já em píxeis da cena. */
export interface SawBladeView {
  /**
   * Qual das lâminas desta nave, de `0` a `3`.
   *
   * A IDENTIDADE, e é ela que faz a lâmina deste quadro ser a mesma do quadro
   * anterior em vez de uma nova. Sem ela, um sistema com pool não distinguiria
   * "quatro lâminas" de "quatro lâminas diferentes sessenta vezes por segundo".
   */
  readonly index: number;
  readonly x: number;
  readonly y: number;
  /**
   * O passo angular autoritativo, em graus inteiros de `[0, 360)`.
   *
   * A rotação sai DAQUI e nunca de um relógio local: seis clientes desenham o
   * mesmo facto, e um `scene.time.now` daria a cada um a sua própria órbita.
   */
  readonly step: number;
  /** O raio DESENHADO, que é também o raio de colisão da simulação. */
  readonly radius: number;
}

/* -------------------------------------------------------------------------- */
/*  A geometria                                                               */
/* -------------------------------------------------------------------------- */

/** Quantos dentes uma lâmina tem. **Sete**, contados na arte da joia. */
export const SAW_TEETH = 7;

/**
 * **Quantas voltas a lâmina dá sobre o próprio eixo em cada volta da órbita.**
 *
 * ## O defeito que isto corrige
 *
 * Três, e era **um**. Com um, a rotação desenhada era exatamente o ângulo
 * orbital: a lâmina rodava uma vez por volta, portanto mantinha sempre a mesma
 * face virada para a nave - travada como a Lua está para a Terra. Vista de fora
 * ela não girava, era CARREGADA. A avaliação manual apanhou-o em uma frase: "as
 * lâminas precisam ter rotação no seu próprio eixo".
 *
 * Com três, sobram duas voltas de giro PRÓPRIO em relação ao caminho, e a
 * lâmina lê-se a girar enquanto é levada à volta do casco.
 *
 * ## Porque três e não trinta
 *
 * Porque a lâmina tem **simetria de sete**: ela repete-se a si mesma a cada
 * 360/7 graus, portanto o que o olho vê passar é a frequência dos DENTES e não a
 * das voltas. A 60 quadros por segundo, um dente que passe acima dos 30 Hz
 * inverte - a roda de carroça - e acima disso borra.
 *
 * | nível | volta da órbita | rotação total | dentes por segundo |
 * | --- | --- | --- | --- |
 * | 1 | 1 440 ms | 2,08 voltas/s | 14,6 Hz |
 * | 30 | 720 ms | 4,17 voltas/s | 29,2 Hz |
 *
 * Ao nível 30 fica logo abaixo da inversão, que é onde uma serra deve estar: a
 * girar depressa e ainda a ler-se. Uma constante maior punha-a do outro lado, e
 * uma serra que aparenta girar ao contrário é pior do que uma que não gira.
 *
 * ## Porque é uma CONSTANTE e não um relógio
 *
 * Porque a rotação tem de continuar a ser derivável do estado autoritativo. O
 * passo vem da sala; isto multiplica-o. Seis clientes desenham a mesma lâmina no
 * mesmo ângulo, e uma reconexão não a apanha noutro sítio.
 */
export const SAW_SPIN_TURNS_PER_ORBIT = 3;

/**
 * O lado da textura, em píxeis.
 *
 * Gerada uma vez e reescalada por lâmina - uma textura para as quatro lâminas de
 * todas as naves de uma arena.
 *
 * Subiu de 128 para 192 quando a lâmina cresceu: a serra passou a ocupar ~23
 * unidades de mundo em vez de ~18, e uma textura que era folgada para a primeira
 * medida passava a ser justa para a segunda com a câmara aproximada. Os dentes e
 * a farpa são a coisa que tem de se ler, e um dente amostrado ao limite lê-se
 * como um disco - que é exatamente o que a arte da joia não é.
 *
 * O custo é uma textura de 192x192 no atlas, gerada uma vez por gestor.
 */
export const SAW_TEXTURE_SIZE = 192;

/** Onde o dente começa, em fração do raio. O cubo é vazado por dentro disto. */
const HUB_FRACTION = 0.13;

/** Quanto do arco um dente varre, em radianos. Dá-lhe a curvatura de foice. */
const TOOTH_SWEEP = 1.24;

/** A largura do dente na base, em fração do raio. */
const TOOTH_WIDTH = 0.46;

/** Onde a farpa secundária nasce ao longo do dente, em fração do comprimento. */
const BARB_AT = 0.6;

/** Quanto a farpa SOBRESSAI para trás, em fração do raio da lâmina. */
const BARB_DEPTH = 0.3;

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * **Um dente, como um polígono fechado.**
 *
 * A construção é uma espinha em espiral com uma largura que cai a **zero
 * exacto** na ponta, e as duas bordas encontram-se lá no mesmo ponto - por
 * construção e não por aproximação. É isso que produz uma ponta aguda em vez da
 * tampa redonda que um traço com espessura constante daria.
 *
 * A borda côncava volta com uma FARPA: um bico que sobressai em `BARB_AT`, e é
 * ele que faz a silhueta ler-se como serrilhada. Sem ele a forma é uma hélice, e
 * uma hélice não é uma serra.
 *
 * Calculada e não desenhada, deliberadamente: uma prova pode medir os sete
 * dentes, contar as farpas e verificar que a ponta fecha em zero sem abrir um
 * canvas - que é o que `saw-blade.test.ts` faz.
 *
 * @param index qual dos {@link SAW_TEETH}.
 * @param radius o raio da lâmina, em píxeis da textura.
 * @param samples quantos pontos por borda.
 */
export function sawTooth(index: number, radius: number, samples = 26): Point[] {
  const base = (index / SAW_TEETH) * Math.PI * 2;
  const hub = radius * HUB_FRACTION;
  const leading: Point[] = [];
  const trailing: Point[] = [];
  for (let i = 0; i <= samples; i += 1) {
    const t = i / samples;
    /*
     * O raio cresce mais depressa no princípio (`t^0.72`), o que empurra a massa
     * do dente para fora do cubo e deixa o centro vazado - é a espiral aberta
     * que a arte tem, e não um disco cheio com recortes.
     */
    const r = hub + (radius - hub) * t ** 0.72;
    const angle = base + t * TOOTH_SWEEP;
    /*
     * A largura cai a ZERO na ponta. O expoente 1.35 mantém a base cheia e faz o
     * último quarto afinar depressa, que é a diferença entre uma lasca e uma
     * agulha.
     */
    const width = radius * TOOTH_WIDTH * (1 - t) ** 1.35;
    /*
     * A FARPA: um bico que SOBRESSAI da borda côncava, na metade exterior.
     *
     * Para fora e não para dentro, e é a correção que fez a forma ler-se: uma
     * mordida para dentro estreita o dente e desaparece por baixo do dente
     * seguinte, porque eles sobrepõem-se junto ao cubo. Um bico para trás fica
     * contra o fundo, e é ele que diz "serra" em vez de "hélice".
     *
     * Em `BARB_AT = 0,6` pela mesma razão: na metade exterior a borda de trás
     * está exposta. E a magnitude é fração do RAIO e não da largura - a largura
     * já vai a zero ali, e um bico proporcional a ela seria invisível.
     */
    const near = Math.abs(t - BARB_AT);
    const barb = near < 0.13 ? (1 - near / 0.13) ** 0.7 : 0;
    const inner = width + radius * BARB_DEPTH * barb;
    const nx = Math.cos(angle);
    const ny = Math.sin(angle);
    /* A normal do arco: perpendicular ao raio, que é onde a espessura vive. */
    const tx = -ny;
    const ty = nx;
    leading.push({ x: nx * r + tx * (width / 2), y: ny * r + ty * (width / 2) });
    trailing.push({ x: nx * r - tx * (inner / 2), y: ny * r - ty * (inner / 2) });
  }
  return [...leading, ...trailing.reverse()];
}

/** A borda de ATAQUE de um dente, sozinha: é onde o fio de luz corre. */
export function sawToothEdge(index: number, radius: number, samples = 26): Point[] {
  const whole = sawTooth(index, radius, samples);
  const half = samples + 1;
  const leading = whole.slice(0, half);
  /*
   * Um fio com espessura, e não um traço: uma faixa fina paralela à borda de
   * ataque, que fecha nas duas pontas. Um `lineStyle` daria tampas redondas, que
   * é exactamente o que a arte não tem.
   */
  const inner = leading.map((point, i) => {
    const t = i / samples;
    const shrink = 1 - 0.05 * (1 - t) ** 0.45;
    return { x: point.x * shrink, y: point.y * shrink };
  });
  return [...leading, ...inner.reverse()];
}

/** Os sete dentes, na ordem em que são preenchidos. */
export function sawOutline(radius: number, samples = 26): Point[][] {
  const teeth: Point[][] = [];
  for (let index = 0; index < SAW_TEETH; index += 1) {
    teeth.push(sawTooth(index, radius, samples));
  }
  return teeth;
}

/**
 * O quão longe do centro a silhueta chega, em píxeis.
 *
 * Medido e não assumido: a ponta de um dente cai exactamente em `radius`, e a
 * largura na base empurra a borda de ataque um pouco para fora do arco. É este
 * número que a escala de uma lâmina usa como denominador, para o raio que a
 * simulação decidiu ser o raio que se vê.
 */
export function sawSpan(radius: number, samples = 26): number {
  let furthest = 0;
  for (const tooth of sawOutline(radius, samples)) {
    for (const point of tooth) {
      const distance = Math.hypot(point.x, point.y);
      if (distance > furthest) {
        furthest = distance;
      }
    }
  }
  return furthest;
}

/* -------------------------------------------------------------------------- */
/*  As cores                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A assinatura: obsidiana e luz fria, e mais nada.
 *
 * Tiradas da arte da joia: o corpo é preto azulado, o fio é quase branco, e o
 * brilho tem um resto frio. Nunca uma cor de fogo, de veneno, de cura ou de
 * escudo - a cor é metade do que diz ao jogador qual dos efeitos está a
 * acontecer, e a Serrilhada é a preta.
 */
export const SAW_BODY = 0x0b0b14;
/** O interior polido, um degrau acima do corpo. Dá-lhe volume sem o iluminar. */
export const SAW_SHEEN = 0x2a3244;
/** O fio, quase branco. É ele que faz a lâmina ler-se contra um fundo escuro. */
export const SAW_EDGE = 0xeef4ff;

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface SawBladeGraphics {
  fillStyle(color: number, alpha?: number): SawBladeGraphics;
  fillPoints(
    points: readonly { x: number; y: number }[],
    closeShape?: boolean,
    closePath?: boolean,
  ): SawBladeGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

export interface SawBladeSprite {
  x: number;
  y: number;
  rotation: number;
  visible: boolean;
  setDepth(value: number): SawBladeSprite;
  setScale(x: number, y?: number): SawBladeSprite;
  setVisible(value: boolean): SawBladeSprite;
  destroy(): void;
}

export interface SawBladeScene {
  readonly textures: { exists(key: string): boolean };
  make: { graphics(config?: unknown, addToScene?: boolean): SawBladeGraphics };
  add: { image(x: number, y: number, key: string): SawBladeSprite };
}

const TEXTURE = 'saw-blade';

/**
 * A textura, criada UMA vez por gestor de texturas e reutilizada para sempre.
 *
 * `textures.exists` antes de desenhar, porque uma cena reiniciada partilha o
 * gestor do jogo: regenerar seria pagar o mesmo desenho a cada abertura de
 * arena. Uma textura para as quatro lâminas de todas as naves - a diferença
 * entre elas é rotação e posição, que são propriedades de sprite.
 */
export function ensureSawTexture(scene: SawBladeScene): void {
  if (scene.textures.exists(TEXTURE)) {
    return;
  }
  const half = SAW_TEXTURE_SIZE / 2;
  const radius = half * 0.94;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const shift = (points: readonly Point[]): Point[] =>
    points.map((point) => ({ x: point.x + half, y: point.y + half }));

  /* O CORPO: sete dentes pretos, e o centro fica vazado por construção. */
  graphics.fillStyle(SAW_BODY, 1);
  for (const tooth of sawOutline(radius)) {
    graphics.fillPoints(shift(tooth), true);
  }
  /*
   * O POLIMENTO: os mesmos dentes, um pouco encolhidos, num azul de aço.
   *
   * Encolhidos para o preto sobreviver como contorno - é ele que separa a lâmina
   * do fundo - e é o que dá a leitura de metal polido em vez de recorte chapado.
   */
  graphics.fillStyle(SAW_SHEEN, 1);
  for (const tooth of sawOutline(radius * 0.93)) {
    graphics.fillPoints(shift(tooth), true);
  }
  /* O FIO: uma faixa quase branca sobre a borda de ataque de cada dente. */
  graphics.fillStyle(SAW_EDGE, 1);
  for (let index = 0; index < SAW_TEETH; index += 1) {
    graphics.fillPoints(shift(sawToothEdge(index, radius)), true);
  }
  graphics.generateTexture(TEXTURE, SAW_TEXTURE_SIZE, SAW_TEXTURE_SIZE);
  graphics.destroy();
}

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Quantas lâminas podem estar no ecrã ao mesmo tempo. **Vinte e quatro.**
 *
 * DERIVADO do máximo legítimo e nunca escolhido por parecer suficiente: seis
 * naves num 3x3, quatro lâminas cada uma ao nível 30. Não há configuração legal
 * que produza uma vigésima quinta - o nível para nos 30, e uma skin tem uma joia
 * de cada tipo.
 *
 * O pool cresce por PROCURA: um duelo sem esta joia aloca zero sprites, e um com
 * uma nave de nível 1 aloca um. O teto só limita o crescimento.
 */
export const SAW_BLADE_POOL = 24;

interface Live {
  readonly sprite: SawBladeSprite;
  /** A identidade `dono:índice`, para a mesma lâmina reusar o mesmo sprite. */
  key: string;
  active: boolean;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * ## Porque ele é SINCRONIZADO e não animado
 *
 * Porque as lâminas são ESTADO, e não acontecimentos. Um corte de Ciclone
 * acontece e acaba, e um sistema de partículas é a forma certa para ele; uma
 * serra existe enquanto a nave existir, e o que a cena tem de fazer a cada
 * quadro é pôr o sprite onde a autoridade diz que a lâmina está.
 *
 * Então não há tween nenhum aqui. Nada interpola, nada decide uma velocidade e
 * nada tem um relógio: {@link SawBladeSystem.sync} recebe a lista autoritativa e
 * escreve posição, rotação e escala. Uma lâmina que desaparece do estado -
 * porque a nave morreu, porque a joia saiu, porque o jogador voltou ao menu -
 * desaparece do ecrã **no mesmo quadro**.
 */
export class SawBladeSystem {
  private readonly pool: Live[] = [];
  private disposed = false;

  constructor(
    private readonly scene: SawBladeScene,
    private readonly depth = 880,
  ) {
    ensureSawTexture(scene);
  }

  /**
   * **Põe o ecrã exactamente igual ao estado autoritativo.**
   *
   * Uma entidade visual por lâmina autoritativa, nem mais nem menos. As chaves
   * que já não vêm são escondidas no mesmo quadro; as que vêm são movidas.
   *
   * Não aloca por quadro: o pool é percorrido, os slots são reusados por chave,
   * e um slot novo só nasce quando aparece uma lâmina a mais do que já houve.
   *
   * @param blades a lista autoritativa, com a chave estável de cada lâmina.
   */
  sync(blades: readonly (SawBladeView & { readonly owner: string })[]): void {
    if (this.disposed) {
      return;
    }
    for (const live of this.pool) {
      live.active = false;
    }
    for (const blade of blades) {
      const key = `${blade.owner}:${blade.index}`;
      const live = this.take(key);
      live.key = key;
      live.active = true;
      live.sprite.x = blade.x;
      live.sprite.y = blade.y;
      /*
       * A ROTAÇÃO: o passo autoritativo vezes {@link SAW_SPIN_TURNS_PER_ORBIT}.
       *
       * Ver a nota dessa constante para o defeito que ela corrige. O que importa
       * aqui é de onde o número vem: do MESMO passo que decide a posição, e de
       * uma constante. Um segundo relógio para o giro próprio - `scene.time.now`,
       * um tween, um `delta` de quadro - daria a cada cliente a sua própria
       * animação para um facto que é um só, e faria a lâmina desenhada deixar de
       * ser derivável do estado que a sala publica.
       */
      live.sprite.rotation = (blade.step * SAW_SPIN_TURNS_PER_ORBIT * Math.PI) / 180;
      /*
       * A ESCALA: o raio que a simulação decidiu, sobre o raio que a textura
       * desenha. O quociente é o factor, e ele multiplica X e Y pelo MESMO
       * valor - uma serra esticada num eixo deixa de ser uma serra.
       *
       * Sem tecto e sem chão. Quem decide o tamanho é a nave, e ela já tem os
       * limites que tem.
       */
      live.sprite.setScale((blade.radius * 2) / SAW_TEXTURE_SIZE);
      live.sprite.setVisible(true);
    }
    for (const live of this.pool) {
      if (!live.active && live.sprite.visible) {
        live.sprite.setVisible(false);
        live.key = '';
      }
    }
  }

  /** Quantas lâminas estão no ecrã agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.pool.filter((entry) => entry.active).length;
  }

  /** Quantos sprites o sistema alocou ao todo. Nunca deve crescer sem uso. */
  pooledCount(): number {
    return this.pool.length;
  }

  /**
   * Apaga tudo o que está no ecrã, sem largar os objectos.
   *
   * Para uma CORREÇÃO ou uma substituição de mundo: o que estava a ser desenhado
   * pertencia a um estado que já não existe.
   */
  clear(): void {
    for (const live of this.pool) {
      live.active = false;
      live.key = '';
      live.sprite.setVisible(false);
    }
  }

  /** Larga tudo. Depois disto o sistema não desenha mais nada. */
  destroy(): void {
    this.disposed = true;
    for (const live of this.pool) {
      live.sprite.destroy();
    }
    this.pool.length = 0;
  }

  /**
   * O slot desta lâmina: o dela se já o tinha, um livre se não, um novo no fim.
   *
   * A procura por chave é o que faz a lâmina 2 da nave `a1` ser sempre o mesmo
   * sprite - o que importa no dia em que alguém lhe quiser pendurar um rasto.
   */
  private take(key: string): Live {
    const mine = this.pool.find((entry) => entry.key === key);
    if (mine) {
      return mine;
    }
    const free = this.pool.find((entry) => !entry.active && entry.key === '');
    if (free) {
      return free;
    }
    const stale = this.pool.find((entry) => !entry.active);
    if (stale) {
      return stale;
    }
    const created: Live = {
      sprite: this.scene.add.image(0, 0, TEXTURE).setDepth(this.depth).setVisible(false),
      key,
      active: false,
    };
    this.pool.push(created);
    return created;
  }
}
